import type { ARKit52Morph, NativeVisemeMorph } from "./visemeMapper";

/** Audio-derived mouth shapes, not word/phoneme timestamps. Authored offline. */
export type MouthShape = "A" | "B" | "C" | "D" | "E" | "F" | "G" | "H" | "X";
export interface TimedMouthCue { start: number; end: number; value: MouthShape }
export interface MouthCueTrack {
  version: 1;
  source: "rhubarb";
  audioSha256: string;
  durationSeconds: number;
  cues: readonly TimedMouthCue[];
}

/** Reject malformed, overlapping or unbound tracks before they reach the clock. */
export function parseMouthCueTrack(value: unknown): MouthCueTrack | null {
  if (!value || typeof value !== "object") return null;
  const track = value as Partial<MouthCueTrack>;
  if (track.version !== 1 || track.source !== "rhubarb"
    || typeof track.audioSha256 !== "string" || !/^[a-f0-9]{64}$/.test(track.audioSha256)
    || typeof track.durationSeconds !== "number" || !Number.isFinite(track.durationSeconds)
    || track.durationSeconds <= 0 || track.durationSeconds > 120
    || !Array.isArray(track.cues) || !track.cues.length || track.cues.length > 5000) return null;
  let end = 0;
  const cues: TimedMouthCue[] = [];
  for (const cue of track.cues) {
    if (!cue || typeof cue.start !== "number" || typeof cue.end !== "number"
      || !Number.isFinite(cue.start) || !Number.isFinite(cue.end)
      || cue.start < end - 0.00001 || cue.start < 0 || cue.end <= cue.start
      || cue.end > track.durationSeconds + 0.02 || typeof cue.value !== "string" || !/^[A-HX]$/.test(cue.value)) return null;
    cues.push({ start: cue.start, end: cue.end, value: cue.value });
    end = cue.end;
  }
  return { version: 1, source: "rhubarb", audioSha256: track.audioSha256, durationSeconds: track.durationSeconds, cues };
}

export function mouthShapeAt(track: MouthCueTrack, seconds: number): MouthShape {
  if (!Number.isFinite(seconds) || seconds < 0 || seconds >= track.durationSeconds) return "X";
  let lo = 0, hi = track.cues.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >>> 1, cue = track.cues[mid];
    if (seconds < cue.start) hi = mid - 1;
    else if (seconds >= cue.end) lo = mid + 1;
    else return cue.value;
  }
  return "X";
}

// Rhubarb's broad 2D categories mapped conservatively onto the existing rigs.
// B combines several consonants/EE; H is L-like. These are approximations of
// shape categories, not a claim of one phoneme per cue.
const NATIVE: Record<MouthShape, Partial<Record<NativeVisemeMorph, number>>> = {
  A: { PP: 0.85 }, B: { ih: 0.35, SS: 0.3 }, C: { E: 0.65 },
  D: { aa: 0.72 }, E: { oh: 0.65 }, F: { ou: 0.75 },
  G: { FF: 0.8 }, H: { nn: 0.6 }, X: {},
};
const ARKIT: Record<MouthShape, Partial<Record<ARKit52Morph, number>>> = {
  A: { mouthClose: 0.8, mouthPressLeft: 0.15, mouthPressRight: 0.15 },
  B: { jawOpen: 0.12, mouthStretchLeft: 0.12, mouthStretchRight: 0.12 },
  C: { jawOpen: 0.3, mouthStretchLeft: 0.12, mouthStretchRight: 0.12 },
  D: { jawOpen: 0.5 }, E: { jawOpen: 0.25, mouthFunnel: 0.45 },
  F: { jawOpen: 0.12, mouthPucker: 0.65, mouthFunnel: 0.25 },
  G: { jawOpen: 0.08, mouthRollLower: 0.35, mouthLowerDownLeft: 0.2, mouthLowerDownRight: 0.2 },
  H: { jawOpen: 0.25, tongueOut: 0.12 }, X: {},
};
export const nativeMouthShape = (shape: MouthShape) => NATIVE[shape];
export const arkitMouthShape = (shape: MouthShape) => ARKIT[shape];
